/**
 * Tidy family-tree layout for Atlas (contour packing).
 *
 * Cards are icon-over-name so siblings pack tighter than a side-by-side row.
 * Childless siblings sit in a compact masonry “area”, except the very top
 * level — roots always sit in one sideways row. Cards keep their own height;
 * columns pack independently. Nodes that have their own children keep a
 * full branch beside that area.
 *
 * Rules:
 * - Sibling subtrees never interleave → edges never cross.
 * - A childless folder can tuck next to a fat sibling (only the shared
 *   depths constrain them — usually just the icon row).
 * - When that folder gains children, deeper contours push it back out.
 * - Child rows sit below the parent's *actual* height so wrapped names
 *   never invert connector lines.
 */

const NODE_W = 108;
const NODE_H = 104;
const H_GAP = 18;
const V_GAP = 44;
const GRID_GAP = 7;
const CLUSTER_PAD = 4;
const PAD = 8;
const ICON_W = 48;
const ICON_H = 48;
const TEXT_GAP = 4;

/**
 * Icon box inside the node — centered on top. Connectors meet the glyph
 * at its horizontal center (folder body roof / floor, not the tab).
 */
export const ATLAS_ICON = {
  padX: (NODE_W - ICON_W) / 2,
  padY: PAD,
  width: ICON_W,
  height: ICON_H,
  centerX: NODE_W / 2,
  glyphTopClosed: 6.8 / 24,
  glyphBotClosed: 19.5 / 24,
  glyphTopOpen: 6.5 / 24,
  glyphBotOpen: 20.6 / 24,
};

const NAME_INNER_W = NODE_W - PAD * 2;
const NAME_CHAR_W = 6.2;
const NAME_LINE_H = 14;

/** Prefer a taller grid so leaf rows stay narrow. */
export function leafGridCols(n) {
  if (n <= 1) return 1;
  return Math.min(4, Math.max(1, Math.round(Math.sqrt(n))));
}

/** CSS column: pad + icon + gap + name + pill + pad. */
export function estimateNodeHeight(name = '') {
  const text = String(name || 'New folder');
  const lines = Math.max(1, Math.ceil((text.length * NAME_CHAR_W) / NAME_INNER_W));
  const textH = lines * NAME_LINE_H + 4 + 16;
  return PAD + ICON_H + TEXT_GAP + textH + PAD;
}

/**
 * @param {Array<{ id: string, name: string, parentId?: string|null, docCount?: number }>} folders
 * @param {{ heights?: Map<string, number> }} [opts]
 */
export function layoutFolderTree(folders = [], opts = {}) {
  const heights = opts.heights instanceof Map ? opts.heights : null;
  const list = Array.isArray(folders) ? folders : [];
  const byParent = new Map();
  for (const f of list) {
    const pid = f.parentId || null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(f);
  }
  for (const kids of byParent.values()) {
    kids.sort((a, b) => list.indexOf(a) - list.indexOf(b));
  }

  const heightOf = (folder) => {
    const measured = heights?.get(folder.id);
    if (measured > 0) return Math.max(NODE_H, Math.round(measured));
    return Math.max(NODE_H, estimateNodeHeight(folder.name));
  };

  const build = (folder) => ({
    folder,
    children: (byParent.get(folder.id) || []).map(build),
    childUnits: [],
    x: 0,
    y: 0,
    h: NODE_H,
  });

  const empty = {
    nodes: [],
    edges: [],
    clusters: [],
    bounds: { minX: 0, minY: 0, maxX: 400, maxY: 200 },
    nodeWidth: NODE_W,
    nodeHeight: NODE_H,
  };

  const roots = (byParent.get(null) || []).map(build);
  if (roots.length === 0) return empty;

  const shift = (tree, dx) => {
    tree.x += dx;
    if (tree.childUnits) {
      for (const u of tree.childUnits) {
        if (u.type === 'cluster') u.x += dx;
      }
    }
    for (const c of tree.children) shift(c, dx);
  };

  const minXIn = (tree) => {
    let m = tree.x;
    for (const c of tree.children) m = Math.min(m, minXIn(c));
    return m;
  };

  const normalize = (tree) => {
    shift(tree, -minXIn(tree));
  };

  const clusterRight = (unit) => unit.x + unit.w + CLUSTER_PAD;
  const clusterLeft = (unit) => unit.x - CLUSTER_PAD;

  const walkChildUnits = (tree, depth, visitCluster, visitNode) => {
    if (tree.childUnits && tree.childUnits.length > 0) {
      for (const u of tree.childUnits) {
        if (u.type === 'cluster') visitCluster(u, depth + 1);
        else visitNode(u.tree, depth + 1);
      }
      return true;
    }
    return false;
  };

  /** Grid rows are extra depths so a tall area cannot be tucked under by a neighbour. */
  const rightContour = (tree, depth, cont) => {
    const r = tree.x + NODE_W;
    cont[depth] = cont[depth] === undefined ? r : Math.max(cont[depth], r);
    if (walkChildUnits(
      tree,
      depth,
      (u, d0) => {
        const rgt = clusterRight(u);
        for (let row = 0; row < u.rows; row++) {
          const d = d0 + row;
          cont[d] = cont[d] === undefined ? rgt : Math.max(cont[d], rgt);
        }
      },
      (child, d) => rightContour(child, d, cont),
    )) return;
    for (const c of tree.children) rightContour(c, depth + 1, cont);
  };

  const leftContour = (tree, depth, cont) => {
    const l = tree.x;
    cont[depth] = cont[depth] === undefined ? l : Math.min(cont[depth], l);
    if (walkChildUnits(
      tree,
      depth,
      (u, d0) => {
        const left = clusterLeft(u);
        for (let row = 0; row < u.rows; row++) {
          const d = d0 + row;
          cont[d] = cont[d] === undefined ? left : Math.min(cont[d], left);
        }
      },
      (child, d) => leftContour(child, d, cont),
    )) return;
    for (const c of tree.children) leftContour(c, depth + 1, cont);
  };

  const shiftUnit = (unit, dx) => {
    if (unit.type === 'cluster') {
      unit.x += dx;
      for (const t of unit.trees) shift(t, dx);
      return;
    }
    shift(unit.tree, dx);
  };

  const normalizeUnit = (unit) => {
    if (unit.type === 'cluster') {
      const min = Math.min(unit.x, ...unit.trees.map((t) => t.x));
      shiftUnit(unit, -min);
      return;
    }
    normalize(unit.tree);
  };

  const rightContourUnit = (unit, depth, cont) => {
    if (unit.type === 'cluster') {
      const rgt = clusterRight(unit);
      for (let r = 0; r < unit.rows; r++) {
        cont[depth + r] = cont[depth + r] === undefined ? rgt : Math.max(cont[depth + r], rgt);
      }
      return;
    }
    rightContour(unit.tree, depth, cont);
  };

  const leftContourUnit = (unit, depth, cont) => {
    if (unit.type === 'cluster') {
      const l = clusterLeft(unit);
      for (let r = 0; r < unit.rows; r++) {
        cont[depth + r] = cont[depth + r] === undefined ? l : Math.min(cont[depth + r], l);
      }
      return;
    }
    leftContour(unit.tree, depth, cont);
  };

  const neededPushUnits = (placed, next) => {
    const rc = {};
    for (const u of placed) rightContourUnit(u, 0, rc);
    const lc = {};
    leftContourUnit(next, 0, lc);
    let push = 0;
    for (const key of Object.keys(rc)) {
      const d = Number(key);
      if (lc[d] === undefined) continue;
      push = Math.max(push, rc[d] + H_GAP - lc[d]);
    }
    return push;
  };

  const packUnits = (units) => {
    if (units.length === 0) return;
    normalizeUnit(units[0]);
    const placed = [units[0]];
    for (let i = 1; i < units.length; i++) {
      normalizeUnit(units[i]);
      shiftUnit(units[i], neededPushUnits(placed, units[i]));
      placed.push(units[i]);
    }
  };

  const partitionUnits = (trees) => {
    const units = [];
    let run = [];
    const flush = () => {
      if (run.length === 0) return;
      if (run.length === 1) units.push({ type: 'node', tree: run[0] });
      else units.push({ type: 'cluster', trees: run.slice() });
      run = [];
    };
    for (const t of trees) {
      if (t.children.length === 0) run.push(t);
      else {
        flush();
        units.push({ type: 'node', tree: t });
      }
    }
    flush();
    return units;
  };

  const layoutCluster = (unit) => {
    const n = unit.trees.length;
    const cols = leafGridCols(n);
    const colH = Array(cols).fill(0);
    unit.cols = cols;
    unit.x = 0;
    unit.w = cols * NODE_W + (cols - 1) * GRID_GAP;
    unit.trees.forEach((t) => {
      let col = 0;
      for (let i = 1; i < cols; i++) {
        if (colH[i] < colH[col]) col = i;
      }
      const h = heightOf(t.folder);
      t.clusterCol = col;
      t.clusterLocalY = colH[col];
      t.clusterRow = colH[col] === 0 ? 0 : 1;
      t.x = col * (NODE_W + GRID_GAP);
      t.h = h;
      colH[col] += h + GRID_GAP;
    });
    unit.h = Math.max(0, ...colH.map((h) => h - GRID_GAP));
    unit.rows = Math.max(1, Math.ceil(unit.h / NODE_H));
  };

  const centerOverChildren = (tree) => {
    const xs = tree.children.map((c) => c.x);
    const ax = ATLAS_ICON.centerX;
    tree.x = (Math.min(...xs) + ax + Math.max(...xs) + ax) / 2 - ax;
  };

  const layoutX = (tree) => {
    if (tree.children.length === 0) {
      tree.x = 0;
      tree.childUnits = [];
      return;
    }
    for (const c of tree.children) layoutX(c);
    tree.childUnits = partitionUnits(tree.children);
    for (const u of tree.childUnits) {
      if (u.type === 'cluster') layoutCluster(u);
    }
    packUnits(tree.childUnits);
    centerOverChildren(tree);
  };

  const assignY = (units, y) => {
    for (const u of units) {
      if (u.type === 'cluster') {
        u.y = y;
        for (const t of u.trees) {
          t.h = heightOf(t.folder);
          t.y = y + (t.clusterLocalY || 0);
        }
        u.h = Math.max(...u.trees.map((t) => t.y + t.h)) - y;
        u.rows = Math.max(1, Math.ceil(u.h / NODE_H));
      } else {
        layoutY(u.tree, y);
      }
    }
  };

  const layoutY = (tree, y) => {
    tree.y = y;
    tree.h = heightOf(tree.folder);
    if (tree.children.length === 0) return;
    assignY(tree.childUnits, y + tree.h + V_GAP);
  };

  for (const r of roots) layoutX(r);
  const rootUnits = roots.map((tree) => ({ type: 'node', tree }));
  packUnits(rootUnits);
  assignY(rootUnits, 0);

  const nodes = [];
  const edges = [];
  const clusters = [];

  const collectClusters = (units, parentId) => {
    for (const u of units) {
      if (u.type === 'cluster') {
        clusters.push({
          id: `cluster:${parentId || 'root'}:${u.trees[0].folder.id}`,
          parentId: parentId || null,
          x: u.x - CLUSTER_PAD,
          y: u.y - CLUSTER_PAD,
          width: u.w + CLUSTER_PAD * 2,
          height: u.h + CLUSTER_PAD * 2,
        });
      } else {
        collectClusters(u.tree.childUnits || [], u.tree.folder.id);
      }
    }
  };
  collectClusters(rootUnits, null);

  const emit = (tree, parentId) => {
    nodes.push({
      id: tree.folder.id,
      name: tree.folder.name,
      parentId: tree.folder.parentId || null,
      docCount: tree.folder.docCount ?? 0,
      kind: tree.folder.kind || 'folder',
      x: tree.x,
      y: tree.y,
      width: NODE_W,
      height: tree.h,
    });
    const hideStem = parentId && tree.clusterRow > 0;
    if (parentId && !hideStem) {
      edges.push({
        id: `${parentId}->${tree.folder.id}`,
        source: parentId,
        target: tree.folder.id,
      });
    }
    for (const c of tree.children) emit(c, tree.folder.id);
  };

  for (const r of roots) emit(r, null);

  const bounds = nodes.reduce(
    (b, n) => ({
      minX: Math.min(b.minX, n.x),
      minY: Math.min(b.minY, n.y),
      maxX: Math.max(b.maxX, n.x + n.width),
      maxY: Math.max(b.maxY, n.y + n.height),
    }),
    { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
  );
  for (const c of clusters) {
    bounds.minX = Math.min(bounds.minX, c.x);
    bounds.minY = Math.min(bounds.minY, c.y);
    bounds.maxX = Math.max(bounds.maxX, c.x + c.width);
    bounds.maxY = Math.max(bounds.maxY, c.y + c.height);
  }

  const ox = bounds.minX - 48;
  const oy = Math.max(0, bounds.minY - 32);
  for (const n of nodes) {
    n.x -= ox;
    n.y -= oy;
  }
  for (const c of clusters) {
    c.x -= ox;
    c.y -= oy;
  }

  return {
    nodes,
    edges,
    clusters,
    bounds: {
      minX: 48,
      minY: 32,
      maxX: bounds.maxX - ox,
      maxY: bounds.maxY - oy,
    },
    nodeWidth: NODE_W,
    nodeHeight: NODE_H,
  };
}

export const ATLAS_NODE = {
  width: NODE_W,
  height: NODE_H,
  hGap: H_GAP,
  vGap: V_GAP,
  gridGap: GRID_GAP,
};
